/**
 * MacroRow / MacroGroup — ported from the OnFlow Design System project
 * (v2/components/track/MacroRow.jsx).
 *
 * One macro: name, value/target, and a thick rounded bar that overshoots into
 * the off-track colour when you go over. v1's 4px square track read as a
 * settings-screen progress bar; this reads as a channel filling up.
 */
import type { CSSProperties } from 'react';

export type MacroChannel = 'protein' | 'carbs' | 'fat';

const CH: Record<MacroChannel, string> = {
  protein: 'var(--macro-protein)',
  carbs: 'var(--macro-carbs)',
  fat: 'var(--macro-fat)',
};

export interface MacroValue {
  current: number;
  target: number;
}

interface MacroRowProps {
  channel?: MacroChannel;
  label?: string;
  current?: number;
  target?: number;
  unit?: string;
  style?: CSSProperties;
}

export function MacroRow({
  channel = 'protein',
  label,
  current = 0,
  target = 100,
  unit = 'g',
  style,
}: MacroRowProps) {
  const pct = target > 0 ? (current / target) * 100 : 0;
  const over = pct > 105;
  const c = over ? 'var(--off-track)' : CH[channel];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--ink-3)',
          }}
        >
          {label || channel}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            fontVariantNumeric: 'tabular-nums',
            color: over ? 'var(--off-track)' : 'var(--ink-1)',
          }}
        >
          {Math.round(current)}
          <span style={{ color: 'var(--ink-3)', fontWeight: 600 }}>
            {' '}
            / {target}
            {unit}
          </span>
        </span>
      </div>
      <div
        style={{
          position: 'relative',
          height: 8,
          borderRadius: 999,
          background: 'var(--paper-3)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: '0 auto 0 0',
            width: `${Math.min(100, pct)}%`,
            borderRadius: 999,
            background: `linear-gradient(90deg, color-mix(in srgb, ${c} 70%, transparent) 0%, ${c} 100%)`,
            transition: 'width var(--t-arc) var(--ease-out)',
          }}
        />
        {over && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 'calc(100% - 3px)',
              width: 3,
              background: 'var(--ink-1)',
              opacity: 0.6,
            }}
          />
        )}
      </div>
    </div>
  );
}

interface MacroGroupProps {
  protein: MacroValue;
  carbs: MacroValue;
  fat: MacroValue;
  style?: CSSProperties;
}

/** Three macro rows in the fixed product order. */
export function MacroGroup({ protein, carbs, fat, style }: MacroGroupProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, ...style }}>
      <MacroRow
        channel="protein"
        label="Protein"
        current={protein.current}
        target={protein.target}
      />
      <MacroRow channel="carbs" label="Carbs" current={carbs.current} target={carbs.target} />
      <MacroRow channel="fat" label="Fat" current={fat.current} target={fat.target} />
    </div>
  );
}
